/**
 * savedView mixin — persist and restore a report's view state (filters, sort,
 * visible columns) to localStorage so users don't re-set filters every visit.
 *
 * Jennifer / Curana request (#10): "save the last thing I did" / an explicit
 * "Save View" across the external reports. Kept client-side (localStorage) —
 * no backend user-preference table required.
 *
 * USAGE in a component:
 *   import savedView from '@/mixins/savedView'
 *   export default {
 *     mixins: [savedView],
 *     data() { return { selectedFacility: null, sortKey: 'events', ... } },
 *     savedView: {
 *       key: 'toc-chained-episodes',          // unique per report
 *       fields: ['selectedFacility', 'selectedYear', 'sortKey', 'sortDir',
 *                'hospiceOnly', 'readmitOnly'],
 *       scopeProp: 'entityId',                 // optional — scope per entity
 *     },
 *   }
 *
 * The mixin:
 *   - restores saved field values on mount (before your own fetch)
 *   - exposes saveView() and resetView() methods
 *   - exposes hasSavedView (bool) and savedViewJustSaved (transient flag for a
 *     "Saved ✓" affordance)
 *   - a component may add a `@click="saveView"` button anywhere.
 *
 * NOTE: fields must be primitive/serializable (string/number/bool/array of
 * primitives). Objects are stored as-is via JSON — keep them simple.
 */
const STORAGE_PREFIX = 'kairon.savedView.'

function cfg(vm) {
  return vm.$options.savedView || {}
}

function storageKey(vm) {
  const c = cfg(vm)
  if (!c.key) return null
  let k = STORAGE_PREFIX + c.key
  if (c.scopeProp && vm[c.scopeProp] != null) k += '.' + vm[c.scopeProp]
  return k
}

export default {
  data() {
    return {
      hasSavedView: false,
      savedViewJustSaved: false,
    }
  },
  mounted() {
    this.restoreView()
  },
  methods: {
    restoreView() {
      const c = cfg(this)
      const sk = storageKey(this)
      if (!sk || !Array.isArray(c.fields)) return
      let saved
      try {
        saved = JSON.parse(localStorage.getItem(sk) || 'null')
      } catch {
        saved = null
      }
      if (!saved || typeof saved !== 'object') { this.hasSavedView = false; return }
      for (const f of c.fields) {
        if (Object.prototype.hasOwnProperty.call(saved, f)) {
          this[f] = saved[f]
        }
      }
      this.hasSavedView = true
    },
    saveView() {
      const c = cfg(this)
      const sk = storageKey(this)
      if (!sk || !Array.isArray(c.fields)) return
      const snapshot = {}
      for (const f of c.fields) snapshot[f] = this[f]
      try {
        localStorage.setItem(sk, JSON.stringify(snapshot))
        this.hasSavedView = true
        this.savedViewJustSaved = true
        setTimeout(() => { this.savedViewJustSaved = false }, 1800)
      } catch (e) {
        // localStorage full / disabled — fail quietly, view just won't persist.
        console.warn('saveView: could not persist view', e)
      }
    },
    resetView() {
      const sk = storageKey(this)
      if (sk) {
        try { localStorage.removeItem(sk) } catch { /* ignore */ }
      }
      this.hasSavedView = false
      if (typeof this.onResetView === 'function') this.onResetView()
    },
  },
}
